import { Check } from "lucide-react";
import React from "react";
import ProductLogo from "src/components/ui/icons/ProductLogo";


interface OnboardingWizardProps {
  currentStep: number;
  highestStepReached?: number;
  children: React.ReactNode;
  onStepClick?: (step: number) => void;
  brandTheme?: string | null;
}

// Step 0 is Welcome (full-screen, no indicators)
// Steps 1-5 show in the step indicator bar
const STEPS = [
  { name: "Integrations", key: "integrations" },
  { name: "Resources", key: "resources" },
  { name: "Company Context", key: "company-context" },
  { name: "Team", key: "team" },
  { name: "Review", key: "review" },
];

export const OnboardingWizard: React.FC<OnboardingWizardProps> = ({
  currentStep,
  highestStepReached,
  children,
  onStepClick,
  brandTheme,
}) => {
  // Step 0 is the welcome screen (no header/indicators)
  const isWelcomeScreen = currentStep === 0;
  // Adjust index for step indicators (they start at step 1)
  const stepIndicatorIndex = currentStep - 1;
  // Highest step index in the indicator (default to current step)
  const highestIndicatorIndex = (highestStepReached ?? currentStep) - 1;

  // Welcome screen is full-screen without chrome
  if (isWelcomeScreen) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        {children}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <div className="border-b border-accent px-6 py-4">
        <div className="flex items-end gap-2">
          <ProductLogo brandTheme={brandTheme} tierzeroClassName="h-8 w-auto" showPoweredBy poweredByClassName="text-[11px] uppercase text-muted-foreground font-semibold leading-none pb-[8px]" />
        </div>
      </div>

      {/* Step indicators */}
      <div className="flex justify-center py-6 border-b border-accent">
        <div className="flex items-center gap-4">
          {STEPS.map((step, index) => (
            <React.Fragment key={step.key}>
              <button
                type="button"
                className={`flex flex-col items-center min-w-[80px] ${
                  index <= highestIndicatorIndex && index !== stepIndicatorIndex
                    ? "cursor-pointer"
                    : "cursor-default"
                }`}
                onClick={() =>
                  index <= highestIndicatorIndex &&
                  index !== stepIndicatorIndex &&
                  onStepClick?.(index + 1)
                }
                disabled={index > highestIndicatorIndex}
              >
                <div
                  className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                    index < stepIndicatorIndex
                      ? "bg-primary text-white hover:ring-2 hover:ring-primary/50"
                      : index === stepIndicatorIndex
                        ? "bg-primary text-white ring-4 ring-primary/30 scale-110"
                        : index <= highestIndicatorIndex
                          ? "bg-primary/50 text-white hover:ring-2 hover:ring-primary/50"
                          : "bg-accent text-muted-foreground"
                  }`}
                >
                  {index < stepIndicatorIndex ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    index + 1
                  )}
                </div>
                <span
                  className={`text-xs mt-2 whitespace-nowrap ${
                    index === stepIndicatorIndex
                      ? "text-white font-medium"
                      : "text-muted-foreground"
                  }`}
                >
                  {step.name}
                </span>
              </button>
              {index < STEPS.length - 1 && (
                <div
                  className={`w-16 h-0.5 mb-6 ${
                    index < stepIndicatorIndex ? "bg-primary" : "bg-border"
                  }`}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex justify-center px-8 py-8 overflow-auto">
        <div className="w-full max-w-5xl text-white">{children}</div>
      </div>
    </div>
  );
};
